/**
 * Currency-aware money formatter (Ola 3 — multimoneda) for Commerce.
 *
 * Commerce stores money as `amount_cents: number` (ADR-0002). The resource
 * (event / brand / order) carries its own ISO-4217 `currency`; format under the
 * *active UI locale* so grouping and the symbol position follow the reader.
 *
 * COP convention preserved: COP is a zero-decimal currency, so its "cents" are
 * whole pesos — we do NOT divide by 100 for zero-decimal currencies. For
 * 2-decimal currencies (USD, EUR, …) cents are divided by 100.
 */

export const DEFAULT_CURRENCY = 'COP';

const ZERO_DECIMAL = new Set(['COP', 'CLP', 'JPY', 'KRW', 'VND', 'PYG']);

/** Map an i18n language tag → a BCP-47 locale for `Intl`. */
export function localeFromLanguage(language: string | undefined): string {
  const lang = (language ?? 'es').toLowerCase();
  return lang.startsWith('en') ? 'en-US' : 'es-CO';
}

function fractionDigitsFor(currency: string): number {
  return ZERO_DECIMAL.has(currency.toUpperCase()) ? 0 : 2;
}

/**
 * Format an amount stored in MINOR units (cents) under a currency + locale.
 *
 * @param cents    integer amount in minor units (whole pesos for COP)
 * @param currency ISO-4217 code from the resource (event/brand/order)
 * @param locale   BCP-47 locale (use `localeFromLanguage(i18n.language)`)
 * @param fallback string shown when the amount is not finite
 */
export function formatMoneyCents(
  cents: number | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
  locale = 'es-CO',
  fallback = 'Consultar',
): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return fallback;
  }
  const code = (currency || DEFAULT_CURRENCY).toUpperCase();
  const digits = fractionDigitsFor(code);
  const amount = digits === 0 ? cents : cents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${code}`;
  }
}

/** Format an amount in MAJOR units (e.g. already-divided dollars). */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
  locale = 'es-CO',
  fallback = 'Consultar',
): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return fallback;
  }
  const code = (currency || DEFAULT_CURRENCY).toUpperCase();
  const cents = ZERO_DECIMAL.has(code) ? amount : Math.round(amount * 100);
  return formatMoneyCents(cents, code, locale, fallback);
}
